import { motion } from 'framer-motion';
import { Button } from './ui/button';

interface NeonHeroProps {
	title: string;
	subtitle?: string;
	primaryCta?: { label: string; href: string };
	secondaryCta?: { label: string; to: string };
}

export default function NeonHero({ title, subtitle, primaryCta, secondaryCta }: NeonHeroProps) {
	return (
		<section className="relative overflow-hidden bg-secondary text-secondary-foreground">
			<div className="absolute inset-0 bg-radial-faint" />
			<div className="absolute inset-0 bg-radial-cyan" />
			<div className="container relative z-10 py-16 md:py-24">
				<motion.h1 className="text-3xl md:text-5xl font-extrabold tracking-tight text-gradient" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
					{title}
				</motion.h1>
				{subtitle && (
					<motion.p className="mt-3 max-w-2xl text-gray-300" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}>
						{subtitle}
					</motion.p>
				)}
				<motion.div className="mt-6 flex flex-wrap gap-3" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}>
					{primaryCta && (
						<a href={primaryCta.href} target="_blank" rel="noreferrer">
							<Button size="lg" className="animate-pulseborder bg-orangepeel text-black hover:opacity-90">{primaryCta.label}</Button>
						</a>
					)}
					{secondaryCta && (
						<a href={secondaryCta.to}>
							<Button variant="gradient" size="lg">{secondaryCta.label}</Button>
						</a>
					)}
				</motion.div>
			</div>
			<div className="pointer-events-none absolute inset-0 z-0 overflow-hidden">
				<motion.div
					className="absolute top-6 left-6 h-64 w-64 rounded-full bg-royalpurple/35 blur-2xl"
					animate={{ y: [0, 14, 0, -10, 0], x: [0, 8, 0, -6, 0], scale: [1, 1.03, 1, 1.01, 1] }}
					transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
				/>
				<motion.div
					className="absolute bottom-6 right-6 h-64 w-64 rounded-full bg-babyblue/30 blur-2xl"
					animate={{ y: [0, -12, 0, 10, 0], x: [0, -10, 0, 8, 0], scale: [1, 1.04, 1, 1.02, 1] }}
					transition={{ duration: 26, repeat: Infinity, ease: 'easeInOut' }}
				/>
				<motion.div
					className="absolute left-1/2 top-1/2 h-52 w-52 -translate-x-1/2 -translate-y-1/2 rounded-full bg-orangepeel/28 blur-2xl"
					animate={{ y: [-8, 8, -8], x: [6, -6, 6], opacity: [0.25, 0.35, 0.25] }}
					transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut' }}
				/>
			</div>
		</section>
	);
}
